import React, { useState } from 'react';
import {
  Globe,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  Info,
} from 'lucide-react';
import { EgressIPsCoreProps, WidgetTheme } from '../widget/types';
import { useWidgetTheme, mainAppTheme } from '../widget/useWidgetTheme';

const EgressIPsCore: React.FC<EgressIPsCoreProps> = ({
  ips,
  loading,
  error,
  mode,
  theme: themeOverrides,
  onCopySuccess,
  onCopyError,
}) => {
  const widgetTheme = useWidgetTheme();
  const baseTheme = mode === 'modal' ? mainAppTheme : widgetTheme;
  const theme = themeOverrides ? { ...baseTheme, ...themeOverrides } : baseTheme;

  const [copiedIp, setCopiedIp] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const handleCopy = async (ip: string) => {
    try {
      await navigator.clipboard.writeText(ip);
      setCopiedIp(ip);
      onCopySuccess?.('IP address copied');
      setTimeout(() => setCopiedIp(null), 2000);
    } catch {
      onCopyError?.('Failed to copy');
    }
  };

  const handleCopyAll = async () => {
    try {
      const allIps = ips.map(ip => ip.ip_address).join('\n');
      await navigator.clipboard.writeText(allIps);
      setCopiedAll(true);
      onCopySuccess?.('All IPs copied');
      setTimeout(() => setCopiedAll(false), 2000);
    } catch {
      onCopyError?.('Failed to copy');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px 0' }}>
        <Loader2 size={24} style={{ color: theme.colors.primary, animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8, color: theme.colors.textMuted, fontSize: '0.875rem' }}>Loading egress IPs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: theme.colors.errorBg,
        borderRadius: theme.radius.md,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}>
        <AlertCircle size={16} style={{ color: theme.colors.error }} />
        <span style={{ color: theme.colors.error, fontSize: '0.875rem' }}>{error}</span>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: theme.fontSans }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px',
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: theme.radius.md,
          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Globe size={18} style={{ color: '#fff' }} />
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: theme.colors.text }}>
	   Firewall Allowlist            
          </h3>
          <p style={{ margin: 0, fontSize: '0.75rem', color: theme.colors.textMuted }}>
	   IPs to add to your firewall rules            
          </p>
        </div>
      </div>

      {/* Info banner */}
      <div style={{
        padding: '12px 14px',
        backgroundColor: theme.colors.infoBg,
        borderRadius: theme.radius.md,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        marginBottom: '16px',
      }}>
        <Info size={14} style={{ color: theme.colors.primary, marginTop: '2px', flexShrink: 0 }} />
        <span style={{ fontSize: '0.8125rem', color: theme.colors.textMuted, lineHeight: 1.5 }}>
	When you create an Integration App that connects to your data sources — whether using native drivers or ODBC — our servers connect from these IP addresses. Add all of them to your firewall or IP allowlist rules to ensure uninterrupted access. We recommend allowlisting every IP shown here.          
        </span>
      </div>

      {ips.length === 0 ? (
        <div style={{
          padding: '24px',
          textAlign: 'center',
          color: theme.colors.textMuted,
          fontSize: '0.875rem',
          backgroundColor: theme.colors.bgAlt,
          borderRadius: theme.radius.md,
        }}>
          No egress IPs configured for this server.
        </div>
      ) : (
        <>
          {/* IP list */}
          <div style={{
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.radius.md,
            overflow: 'hidden',
          }}>
            {ips.map((ip, idx) => (
              <div
                key={ip.ip_address}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 14px',
                  backgroundColor: theme.colors.bg,
                  borderBottom: idx < ips.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <code style={{
                    fontSize: '0.875rem',
                    fontFamily: theme.fontMono,
                    fontWeight: 600,
                    color: theme.colors.text,
                  }}>
                    {ip.ip_address}
                  </code>
                  {ip.label && (
                    <span style={{
                      fontSize: '0.6875rem',
                      color: theme.colors.textMuted,
                      backgroundColor: theme.colors.bgAlt,
                      padding: '2px 8px',
                      borderRadius: '9999px',
                    }}>
                      {ip.label}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleCopy(ip.ip_address)}
                  title="Copy IP"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: copiedIp === ip.ip_address ? theme.colors.success : theme.colors.textMuted,
                  }}
                >
                  {copiedIp === ip.ip_address ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            ))}
          </div>

          {/* Copy all button */}
          {ips.length > 1 && (
            <button
              onClick={handleCopyAll}
              style={{
                marginTop: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: copiedAll ? theme.colors.success : theme.colors.primary,
                backgroundColor: 'transparent',
                border: `1px solid ${copiedAll ? theme.colors.success : theme.colors.primary}`,
                borderRadius: theme.radius.md,
                cursor: 'pointer',
              }}
            >
              {copiedAll ? <Check size={14} /> : <Copy size={14} />}
              {copiedAll ? 'Copied!' : 'Copy all IPs'}
            </button>
          )}
        </>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default EgressIPsCore;
